import React, { CSSProperties, ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { buildClassName } from 'utils/build-class-name';
import { useIsomorphicLayoutEffect } from 'types/react';
import styles from './style.module.scss';

//#region Types
type ExpandableTextProps = {
  /** The text content to render. Clamped to `maxLines` until the user expands it. */
  children: ReactNode;
  /** Number of lines shown while collapsed. The toggle only appears when the content exceeds this. Defaults to `6`. */
  maxLines?: number;
  /** Optional test id applied to the show more / show less toggle. */
  toggleTestId?: string;
};
//#endregion

//#region Component
/**
 * ExpandableText — clamps long text to `maxLines` and reveals a "Show More" / "Show Less"
 * toggle only when the content actually overflows.
 *
 * The overflow check is measured from the rendered element (not a character count), so the
 * threshold adapts automatically to the available width — desktop and mobile each collapse at
 * their own natural line length. A `ResizeObserver` re-measures on viewport/content changes.
 */
export default function ExpandableText({ children, maxLines = 6, toggleTestId }: ExpandableTextProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const element = contentRef.current;
    // Only measure while collapsed (clamp applied); when expanded we keep the last value so
    // the toggle stays visible to allow collapsing again.
    if (!element || expanded) {
      return;
    }
    const measure = () => setIsOverflowing(element.scrollHeight - element.clientHeight > 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, expanded, maxLines]);

  return (
    <div className={styles.wrapper}>
      <div
        ref={contentRef}
        className={buildClassName(styles.content, !expanded && styles.clamped)}
        style={!expanded ? ({ WebkitLineClamp: maxLines } as CSSProperties) : undefined}
      >
        {children}
      </div>
      {isOverflowing && (
        <button
          type='button'
          className={buildClassName(styles.toggle, !expanded && styles.toggleInline)}
          onClick={() => setExpanded(previous => !previous)}
          aria-expanded={expanded}
          data-testid={toggleTestId}
        >
          {t(expanded ? LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_SHOW_LESS : LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_SHOW_MORE)}
        </button>
      )}
    </div>
  );
}
//#endregion
